import { FC, SyntheticEvent, useEffect, useRef, useState } from "react";
import { MDSChip, MDSChipVariant } from "@/mds/components/chips";
import {
  MDSMenuContentItemKind,
  MDSMenuContentList,
  MDSMenuDropdownPopover,
  MDSMenuItemIconVariant,
} from "@/mds/components/menu";
import { MDSTextFieldSlotName, MDSTextFieldVariant } from "@/mds/components/forms/text-field";
import { StyledContainer, StyledTextField, StyledSelectMenu } from "./chip-input.style";
import { MDSChipInputItem, MDSChipInputProps } from "./chip-input.types";

export const MDSChipInput: FC<MDSChipInputProps> = ({
  isDisabled,
  getChips,
  chips,
  setChips,
  onSubmit,
  placeholder,
  textFieldVariant,
  autoFocus,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const [value, setValue] = useState<string>("");
  const [isHoveringOverAvailableChips, setIsHoveringOverAvailableChips] = useState<boolean>(false);
  const [availableChips, setAvailableChips] = useState<MDSChipInputItem[]>([]);

  useEffect(() => {
    const fetchAvailableChips = async (value: string) => {
      const chips = await getChips(value);
      setAvailableChips(chips);
    };
    fetchAvailableChips(value);
  }, [getChips, value]);

  const contentList: MDSMenuContentList = availableChips
    .sort((a, b) => b.orderByString.localeCompare(a.orderByString))
    .map(chip => ({
      id: chip.id,
      kind: MDSMenuContentItemKind.Item,
      iconVariant: MDSMenuItemIconVariant.Avatar,
      icon: chip.icon ? props => chip.icon?.(props) : undefined,
      label: chip.label,
      subtitle: chip.secondaryLabel,
      onClick: () => {
        handleSelectChip(chip);
        setValue("");
      },
    }));

  const handleSelectChip = (chip: MDSChipInputItem) => {
    setChips(chips => {
      const hasChip = chips.some(c => c.id === chip.id);
      if (hasChip) return chips;
      return [...chips, chip];
    });
    setIsHoveringOverAvailableChips(false);
    inputRef.current?.focus();
  };

  const handleRemoveChip = (chip: MDSChipInputItem) => {
    setChips(chips => chips.filter(c => c.id !== chip.id));
  };

  const handleInputChange = (value: string) => {
    if (value.endsWith(",") || value.endsWith(" ")) {
      const trimmedValue = value.substring(0, value.length - 1).trim();
      const matchingChips = availableChips.filter(chip =>
        chip.searchLabels?.includes(trimmedValue)
      );
      if (matchingChips.length === 1) {
        handleSelectChip(matchingChips[0]);
        setValue("");
        return;
      }
    }
    setValue(value);
  };

  const handleKeydown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case "Enter": {
        if (value.length === 0 && chips.length > 0) {
          e.preventDefault();
          onSubmit(chips);
          return;
        }
        break;
      }
      case "Backspace": {
        if (value.length === 0 && chips.length > 0) {
          e.preventDefault();
          handleRemoveChip(chips[chips.length - 1]);
          return;
        }
        break;
      }
    }
    if (availableChips.length === 0 || isHoveringOverAvailableChips) return;

    switch (e.key) {
      case "ArrowUp":
      case "ArrowDown": {
        e.preventDefault();
        menuRef.current?.focus();
        break;
      }
    }
  };

  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case "ArrowUp":
      case "ArrowDown":
      case "Enter": {
        // Allow navigation of the menu
        break;
      }
      default: {
        // Any other key presses should go back to the input
        inputRef.current?.focus();
        break;
      }
    }
  };

  const stopEventPropagation = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <StyledContainer ref={triggerRef}>
      <StyledTextField
        variant={textFieldVariant ?? MDSTextFieldVariant.Default}
        placeholder={chips.length ? undefined : placeholder}
        onKeyDown={handleKeydown}
        value={value}
        onChange={handleInputChange}
        autoFocus={autoFocus}
        isDisabled={isDisabled}
        ref={inputRef}
      >
        {chips.map(chip => (
          <MDSChip
            key={chip.id}
            variant={MDSChipVariant.Medium}
            slot={MDSTextFieldSlotName.LeftAddon}
            icon={chip.icon ? props => chip.icon?.(props) : undefined}
            onRemove={() => handleRemoveChip(chip)}
            isDisabled={isDisabled}
          >
            {chip.label}
          </MDSChip>
        ))}
      </StyledTextField>
      <div onKeyDownCapture={handleMenuKeyDown}>
        <MDSMenuDropdownPopover
          triggerRef={triggerRef}
          isOpen={contentList.length > 0}
          placement="bottom start"
          isNonModal
          onPointerDown={stopEventPropagation}
          onMouseDown={stopEventPropagation}
          onClick={stopEventPropagation}
        >
          <StyledSelectMenu
            ref={menuRef}
            selectionBehavior="toggle"
            selectionMode="single"
            shouldFocusWrap={true}
            contentList={contentList}
          />
        </MDSMenuDropdownPopover>
      </div>
    </StyledContainer>
  );
};
